import useAuthStore from '@/stores/useAuthStore';
import { getEnv } from '@/utils/envUtils';
import {
    HttpTransportType,
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
import { type Ref, ref } from 'vue';

const { VITE_API_BASE } = getEnv();

const MAX_RECONNECT_RETRIES = 5;
const RECONNECT_BASE_DELAY_MS = 1000; // Base delay for reconnection attempts (1 second)
const RECONNECT_MAX_DELAY_MS = 30000; // Maximum delay for reconnection attempts (30 seconds)

let reconnectRetryLimitReached = false;

const connection: Ref<HubConnection | null> = ref(null);

export const getSignalRConnection = async () => {
    const token = await useAuthStore().getToken();
    if (!connection.value && token) {
        const url = `${VITE_API_BASE}/hubs/communication?access_token=${token}`;
        const normalizedUrl = url.trim().endsWith('/') ? url.trim().slice(0, -1) : url.trim();
        connection.value = new HubConnectionBuilder()
            .withUrl(normalizedUrl, {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    if (retryContext.previousRetryCount >= MAX_RECONNECT_RETRIES) {
                        reconnectRetryLimitReached = true;
                        return null; // Stop retrying after max retries
                    }
                    // Incremental retry delay
                    return Math.min(
                        RECONNECT_BASE_DELAY_MS * Math.pow(2, retryContext.previousRetryCount),
                        RECONNECT_MAX_DELAY_MS
                    );
                },
            })
            .configureLogging(getEnv().DEV ? LogLevel.Information : LogLevel.Error)
            .build();
    }
    return connection.value;
};

export const hasSignalRReconnectRetryLimitReached = () => {
    return reconnectRetryLimitReached;
};

export const resetSignalRReconnectRetryLimitReached = () => {
    reconnectRetryLimitReached = false;
};

export const startSignalR = async () => {
    try {
        const conn = await getSignalRConnection();
        if (conn?.state === HubConnectionState.Disconnected) {
            reconnectRetryLimitReached = false;
            await conn.start();
            return true;
        }
    } catch (error) {
        console.error('WS connection start failed.', error);
    }
    return false;
};
